import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { bhPromptConfirmParticipationRemove } from '@adornis/buchungsheld-theme/prompts/bh-prompt-confirm.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { obs } from '@adornis/chemistry/directives/observable.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { html, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { map } from 'rxjs';
import type { ActionItem } from '../../../../buchungsheld/src/client/components/bh-dropdown-action-content.js';
import type { BHParticipationDataview } from '../../../../buchungsheld/src/client/components/bh-participation-dataview.js';
import {
  AppointmentParticipationForBulkUpdate,
  setAppointmentParticipationBulk,
  setParticipationRequirement,
} from '../../api/participation.js';
import { Certificate } from '../../entities/certificate.js';
import { Einzelberatung } from '../../entities/einzelberatung.js';
import { Participation } from '../../entities/Participation.js';
import type { ParticipationExportCSV } from '../../entities/ParticipationExportCSV.js';
import { ParticipationWorkshop } from '../../entities/ParticipationWorkshop.js';
import { Workshop } from '../../entities/workshop.js';
import { ParticipationState } from '../../enums/enums.js';
import { Permission, Role } from '../../enums/roles.js';
import { ParticipationDataview } from '../../use-case-entities/ParticipationsDataview.js';
import { isDefined } from '../../util.js';
import type { ParticipationOverviewPlugin } from '../plugin-types.js';

export function defaultGetDropdownActions(
  host: BHParticipationDataview,
  targetDataList: ParticipationDataview[],
  paymentActive: boolean,
): ActionItem[] {
  if (!targetDataList.length) return [];

  const firstData = targetDataList[0]!;
  const mergableAttr = {
    billToggle: targetDataList.every(
      data => data.participation.ikh_doNotSendInvoice === firstData.participation.ikh_doNotSendInvoice,
    ),
    stateSet: !!targetDataList.filter(data => data.buyer?.email).length,
    manualPaymentReceived: targetDataList.every(
      data => data.has('payment') && data.payment?.status && data.payment.status !== 'paid',
    ),
  };

  const action_toggleInvoices = () => {
    if (!paymentActive || host.mode === 'lecturer') return null;
    return {
      disabled: !mergableAttr.billToggle,
      action: async () => {
        if (!mergableAttr.billToggle) return;
        await XNativeDialog.waitFor(
          (async () => {
            for (const targetData of targetDataList) {
              await Participation.setIkhDoNotSendInvoice(
                targetData.participation._id,
                !targetData.participation.ikh_doNotSendInvoice,
              )().catch(host.e);
            }
          })(),
        );
      },
      icon: 'receipt_long',
      text:
        mergableAttr.billToggle && firstData.participation.ikh_doNotSendInvoice
          ? 'Rechnung aktivieren'
          : 'Rechnung deaktivieren',
    };
  };

  const action_toggleAppointmentParticipation = () => {
    const product = host.product.value;
    if (!product || !(product instanceof Workshop)) return [];
    const appointments = product.appointments;
    const participation = firstData.participation;
    if (!(participation instanceof ParticipationWorkshop)) {
      throw new Error('participation is not instance of ParticipationWorkshop');
    }
    return appointments.map((appointment, i) => {
      const appointmentName = appointment.name || 'Termin ' + (i + 1);
      const fullfilled = !!participation.appointmentParticipations?.find(it => it.appointmentID === appointment._id);
      return {
        icon: 'check_circle',
        text: fullfilled
          ? `hat an ${appointmentName} nicht teilgenommen` //
          : `hat an ${appointmentName} teilgenommen`,
        disabled: true,
        action: async () => {
          await XNativeDialog.waitFor(
            setAppointmentParticipationBulk(
              targetDataList.map(
                data =>
                  new AppointmentParticipationForBulkUpdate({
                    participationID: data.participation._id,
                    appointmentID: appointment._id,
                    fullfilled: !fullfilled,
                  }),
              ),
            )()
              .then(() => host.clearUserSelection())
              .catch(host.e),
          );
        },
      };
    });
  };

  const action_toggleParticipationRequirements = () => {
    const constraints = host.product.value?.settings.extraParticipationConstraints;
    if (!constraints) return [];

    return constraints.map(constraint => {
      const fullfilled = firstData.participation.fulfilledParticipationRequirements?.includes(constraint);
      return {
        disabled: true,
        icon: 'check_circle',
        text: fullfilled ? 'Hat nicht ' + constraint : 'Hat ' + constraint,
        action: async () => {
          await XNativeDialog.waitFor(
            (async () => {
              for (const targetData of targetDataList) {
                console.log({ participationID: targetData.participation._id, constraint, fullfilled });
                await setParticipationRequirement(targetData.participation._id, constraint, !fullfilled, true)();
              }
              host.clearUserSelection();
            })().catch(host.e),
          );
        },
      };
    });
  };

  const action_togglePaymentStatus = () => {
    if (!mergableAttr.manualPaymentReceived) return null;
    return {
      action: async () => {
        if (await XNativeDialog.confirm(host._translation.translate('PAYMENT', 'update_status'))) {
          await XNativeDialog.waitFor(
            (async () => {
              for (const targetData of targetDataList) {
                targetData.payment!.status = 'paid';
              }
              host.clearUserSelection();
            })().catch(host.e),
          );
        }
      },
      icon: 'credit_card',
      text: host._translation.translate('PAYMENT', 'mark_as_paid'),
    };
  };

  const action_sendCertificate = () => {
    const allParticipationsAreMarkedAsParticipated = targetDataList.every(
      data => data.participation.state === ParticipationState.PARTICIPATED,
    );

    if (!allParticipationsAreMarkedAsParticipated) return [];

    return [
      {
        action: async () => {
          const sendITs = targetDataList
            .map(data => data.participation._id)
            .map(id => Certificate.sendCertificateToParticipant(id)());

          const onSuccess = () => {
            host.clearUserSelection();
            void XSnackbar.show(host._translation.translate('workshop', 'successfully_sent_certificates'));
          };

          await XNativeDialog.waitFor(Promise.all(sendITs)).then(onSuccess).catch(host.e);
        },
        icon: 'send',
        text: host._translation.translate('workshop', 'send_certificate'),
      },
    ];
  };

  return [
    action_toggleInvoices(),
    ...action_toggleAppointmentParticipation(),
    ...action_toggleParticipationRequirements(),
    action_togglePaymentStatus(),
    ...action_sendCertificate(),
  ].filter(isDefined);
}

export const defaultParticipationOverviewPlugin: ParticipationOverviewPlugin = {
  id: 'participation-overview::default',
  type: 'participation-overview',
  getDropdownItemActions: defaultGetDropdownActions,
  getColumnDefinitions: (host, product, coloring, paymentActive, participationState, extraColumns) => {
    //find out if user is lecturer and not admin
    const currentUserInfo = host._currentUser.value;
    const callerIsLecturer =
      currentUserInfo?.roles?.some(role => role.name === Role.LECTURER) &&
      !currentUserInfo?.roles?.some(role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN);

    const customColumns: ColumnDefinition<typeof ParticipationDataview>[] = [
      {
        columnWidth: 'max-content',
        customID: 'selection-handle',
        renderHeader: tableView => html`
          ${obs(
            host._selectedUsers.observable.pipe(
              map(
                () => html`
                  <x-flex center crossaxis-center ${css({ padding: `0 ${host.spacing.sm}` })}>
                    ${obs(
                      host._selectedUsers.observable.pipe(
                        map(
                          () => html`
                            <tr-checkbox
                              outlined
                              boolean-only
                              enableicon="adjust"
                              disableicon
                              .coloring=${coloring}
                              @click=${(e: MouseEvent) => e.stopPropagation()}
                              @value-picked=${(e: ValueEvent<boolean>) => {
                                if (e.detail.value === true)
                                  host._selectedUsers.next({
                                    ...host._selectedUsers.value,
                                    [participationState]: tableView.data.map(data =>
                                      ParticipationDataview.resolveID(data),
                                    ),
                                  });
                                if (e.detail.value === false)
                                  host._selectedUsers.next({ ...host._selectedUsers.value, [participationState]: [] });
                              }}
                              .value=${tableView.data.length &&
                              host._selectedUsers.value[participationState].length === tableView.data.length}
                            ></tr-checkbox>
                          `,
                        ),
                      ),
                    )}
                  </x-flex>
                `,
              ),
            ),
          )}
        `,
        renderItem: (entityInstance, viewElement) => {
          const idsCtrl = host._selectedUsers;
          const curID = ParticipationDataview.resolveID(entityInstance);

          return html`
            <tr-flex center crossaxis-center ${css({ margin: `0 ${host.spacing.xs}` })}>
              ${obs(
                host._selectedUsers.observable.pipe(
                  map(allSelectedUsers => allSelectedUsers[participationState]),
                  map(selectedUsers => {
                    const isSelected = selectedUsers?.includes(curID);
                    return html`
                      <tr-checkbox
                        outlined
                        boolean-only
                        enableIcon="adjust"
                        disableIcon=""
                        .coloring=${coloring}
                        .value=${isSelected}
                        @value-picked=${(e: ValueEvent<boolean>) => {
                          if (!idsCtrl.value[participationState]) idsCtrl.value[participationState] = [];
                          if (e.detail.value === false)
                            idsCtrl.next({
                              ...idsCtrl.value,
                              [participationState]: idsCtrl.value[participationState].filter(id => id !== curID),
                            });
                          if (e.detail.value === true)
                            idsCtrl.next({
                              ...idsCtrl.value,
                              [participationState]: idsCtrl.value[participationState].concat([curID]),
                            });
                        }}
                      >
                        ${isSelected ? 'radio_button_checked' : 'radio_button_unchecked'}
                      </tr-checkbox>
                    `;
                  }),
                ),
              )}
            </tr-flex>
          `;
        },
      },

      ...(extraColumns ?? []),
      ...(callerIsLecturer
        ? []
        : [
            {
              columnName: host._translation.translate('PAYMENT', 'payment') as string,
              fieldName: 'payment.status',
              renderItem: instance =>
                html`
                  <x-text>
                    ${(() => {
                      if (!instance.payment?.status) return '-';
                      return host._translation.translate('PAYMENT', 'STATE', instance.payment.status as any);
                    })()}
                  </x-text>
                `,
            },
          ]),
      ...(!callerIsLecturer && product instanceof Workshop
        ? product.appointments?.map((appointment, i) => {
            const appointmentName = appointment.name ?? 'Termin ' + (i + 1);
            return {
              customID: '_appointment_participation_' + appointment._id,
              columnWidth: `CALC( ${host.spacing.lg} + ${host.spacing.xxs} )`,
              columnName: appointmentName + '?',
              selectionSet: (<const>{
                product: { appointments: { name: 1 } },
                participation: { appointmentParticipations: { appointmentID: 1 }, ikh_doNotSendInvoice: 1 },
              }) as any, // TODO: fix this
              renderHeader: _ =>
                host._renderColHeader('check_circle', appointmentName + '?', {
                  margin: `0 -3px 0 3px`,
                }),
              renderItem: instance => {
                const fullfilled = !!instance.participation.appointmentParticipations?.find(
                  ap => ap.appointmentID === appointment._id,
                );
                return html`
                  <x-icon
                    ${css({
                      fontSize: host.spacing.md,
                      fontWeight: fullfilled ? 'bold' : 'unset',
                      color: fullfilled ? host.colors.tone.success : host.colors.tone.error,
                    })}
                  >
                    ${fullfilled ? 'check' : 'close'}
                  </x-icon>
                `;
              },
            };
          })
        : []),
      ...(callerIsLecturer
        ? []
        : product.settings.extraParticipationConstraints?.map((constraint, i) => {
            return {
              customID: '_extra_participation_constraint_' + i,
              columnWidth: `CALC( ${host.spacing.lg} + ${host.spacing.xxs} )`,
              columnName: constraint + '?',
              selectionSet: <const>{ participation: { fulfilledParticipationRequirements: 1 } },
              renderHeader: _ =>
                host._renderColHeader('check_circle', constraint + '?', {
                  margin: `0 -3px 0 3px`,
                }),
              renderItem: instance => {
                const fullfilled = instance.participation.fulfilledParticipationRequirements?.includes(constraint);
                return html`
                  <x-icon
                    ${css({
                      fontSize: host.spacing.md,
                      fontWeight: fullfilled ? 'bold' : 'unset',
                      color: fullfilled ? host.colors.tone.success : host.colors.tone.error,
                    })}
                  >
                    ${fullfilled ? 'check' : 'close'}
                  </x-icon>
                `;
              },
            };
          }) ?? []),
      ...(product instanceof Einzelberatung
        ? [
            {
              columnName: 'Berater',
              fieldName: 'berater.email',
              renderItem: (instance: ParticipationDataview) => {
                return html` <tr-text> ${instance.berater?.email} </tr-text> `;
              },
            },
            {
              columnName: 'Zeitslot',
              fieldName: 'participation.timeslotID',
              renderItem: (instance: ParticipationDataview) => {
                const slot = product.slots.find(slot => slot._id === instance.participation.timeslotID);
                return html`
                  <tr-text>
                    ${slot?.start?.toFormat('dd.LL.yyyy')} <br />
                    ${slot?.start?.toFormat('HH:mm')} - ${slot?.end?.toFormat('HH:mm')}
                  </tr-text>
                `;
              },
            },
          ]
        : []),
      ...byAdminColumn(host),
      ...(paymentActive && !callerIsLecturer
        ? [
            {
              columnName: 'erhält Rechnung',
              fieldName: 'participation.ikh_doNotSendInvoice',
              columnWidth: `CALC( ${host.spacing.lg} + ${host.spacing.xxs} )`,
              renderHeader: _ => host._renderColHeader('receipt_long', 'erhält Rechnung?'),
              renderItem: instance =>
                html`
                  <x-icon
                    ${css({
                      fontSize: host.spacing.md,
                      fontWeight: !instance.participation.ikh_doNotSendInvoice ? 'bold' : 'unset',
                      color: !instance.participation.ikh_doNotSendInvoice
                        ? host.colors.tone.success
                        : host.colors.tone.error,
                    })}
                  >
                    ${!instance.participation.ikh_doNotSendInvoice ? 'check' : 'close'}
                  </x-icon>
                `,
            },
          ]
        : []),
      {
        columnWidth: 'max-content',
        customID: '_options',
        columnName: 'Aktion',
        renderHeader: _ => host._renderColHeader('electric_bolt', 'Teilnahmestatus anpassen'),
        selectionSet: <const>{ participation: { state: 1 } },
        renderItem: instance => {
          const reference = createRef<XDropdown>();

          return html`
            <x-flex horizontal space-between center crossaxis-center>
              <x-button
                  @click=${() => {
                    reference.value?.toggle();
                  }}
                  mode="icon"
                  icon="arrow_drop_down"
                > Trigger Dropdown </x-button>

              <x-dropdown ${ref(reference)}>
                <bh-dropdown-action-content
                  slot="dropdown-content"
                  heading=${host._translation.translate('PARTICIPATION_DATAVIEW', 'modifyParticipationStatus')}
                  .actions=${() => defaultGetDropdownActions(host, [instance], paymentActive)}
                  .dialogRef=${reference}
                ></bh-dropdown-action-content
              </x-dropdown>

              ${
                currentUserInfo?.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID)
                  ? html`
                      <tr-button-small
                        icon="delete"
                        .color=${host.colors.tone.error}
                        .action=${async () =>
                          bhPromptConfirmParticipationRemove(async () => await instance.participation.remove())}
                      ></tr-button-small>
                    `
                  : nothing
              }
            </x-flex>
          `;
        },
      },
    ];

    return customColumns;
  },

  getCSVFieldsForExport: async stammdatenFields => {
    let currentUserInfo = await CurrentUserInfo.getMyself()({
      roles: { _id: 1, name: 1, contexts: 1, permissions: 1 },
    });

    //if lecturer, but not admin, then show only first name, lastname and country of origin
    const callerIsLecturer = currentUserInfo?.roles?.some(role => role.name === Role.LECTURER);
    const isAdmin = currentUserInfo?.roles?.some(role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN);

    return callerIsLecturer && !isAdmin
      ? [
          //non-admins can only export limited user data
          { input: 'Vorname', output: 'user.firstName' },
          { input: 'Nachname', output: 'user.lastName' },
          { input: 'Status', output: 'participation.state' },
        ]
      : [
          { input: 'ID', output: 'participation._id' },
          { input: 'Vorname', output: 'user.firstName' },
          { input: 'Nachname', output: 'user.lastName' },
          { input: 'E-Mail', output: 'user.email' },
          { input: 'Status', output: 'participation.state' },
          new FieldMappingEntry(
            { input: 'Wartelistenplatz' },
            undefined,
            (v: any, data: ParticipationExportCSV, i: number) => {
              if (data.participation.state !== ParticipationState.WAITING) return '-';
              return data.participation?.waitingListPlace ?? '-';
            },
          ),
          new FieldMappingEntry(
            { input: 'Letzte Änderung' },
            undefined,
            (v: any, data: ParticipationExportCSV, i: number) => data.participation.updatedAt.toFormat('dd.LL.yyyy'),
          ),
          ...stammdatenFields.map(field => {
            return new FieldMappingEntry(
              { input: field },
              undefined,
              (v: any, data: ParticipationExportCSV, i: number) => {
                const value = data.stammdaten[field];
                if (value instanceof DateTime) return value.toLocaleString(DateTime.DATE_MED);
                return data.stammdaten[field];
              },
            );
          }),
          /* settings */
          { input: 'Einstellungen ID', output: 'settings._id' },
          { input: 'Kurs Ort', output: 'settings.courseLocation' },
          { input: '1. E-Mail Erinnerung', output: 'settings.firstReminderLeadTime' },
          { input: '2. E-Mail Erinnerung', output: 'settings.secondReminderLeadTime' },
          { input: 'Abmeldezeitraum', output: 'settings.blockSignOffDaysInAdvance' },
          { input: 'Nicht Aufrücken Zeitraum', output: 'settings.blockAutomaticMoveUpDaysInAdvance' },
          { input: 'QR-Code Gültigkeit Dauer (Minuten)', output: 'settings.qrCodeValidityInMinutes' },
        ];
  },
};

// columns
function byAdminColumn(host: BHParticipationDataview) {
  return [
    {
      columnWidth: 'max-content',
      renderHeader: _ => host._renderColHeader('gavel', 'Nutzer wurde durch einen Admin in diesen Status gebracht'),
      fieldName: 'participation.lastStateChangeWasInitiatedByAdmin',
      columnName: 'durch Admin',
      selectionSet: ParticipationDataview.neededGqlFields,
    },
  ];
}
